import BaseFieldValue, {
  type BaseFieldValueProps,
} from '@/components/base/BaseFieldValue';
import { Descriptions, type DescriptionsProps } from 'antd';
import type { CSSProperties, ReactNode } from 'react';

export interface DetailTableItem
  extends Omit<NonNullable<DescriptionsProps['items']>[number], 'children'>,
    Omit<BaseFieldValueProps, 'value' | 'timeZone'> {
  value?: unknown;
  children?: ReactNode;
  render?: (value: unknown) => ReactNode;
}

export interface DetailTableProps
  extends Omit<DescriptionsProps, 'items' | 'column'> {
  items: DetailTableItem[];
  column?: 1 | 2 | 3;
  fontSize?: CSSProperties['fontSize'];
  labelFontSize?: CSSProperties['fontSize'];
}

/** 只读字段详情统一使用标签/内容网格，尾项填满余位，窄屏收为一组。 */
export default function DetailTable({
  items,
  column = 2,
  fontSize,
  labelFontSize,
  className,
  ...props
}: DetailTableProps) {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const renderedItems = items.map((field, index) => {
    const {
      value,
      valueType,
      format,
      valueEnum,
      valueClassName,
      valueSuffix,
      valueStrong,
      fontSize: fieldFontSize,
      render,
      children,
      span,
      ...item
    } = field;
    let content = children;
    if (render) content = render(value);
    else if (children === undefined) {
      content = (
        <BaseFieldValue
          value={value}
          valueType={valueType}
          format={format}
          valueEnum={valueEnum}
          valueClassName={valueClassName}
          valueSuffix={valueSuffix}
          valueStrong={valueStrong}
          fontSize={fieldFontSize ?? fontSize}
          timeZone={timeZone}
        />
      );
    }
    return {
      ...item,
      styles: {
        ...item.styles,
        content: {
          ...item.styles?.content,
          fontSize: fieldFontSize ?? fontSize ?? item.styles?.content?.fontSize,
        },
      },
      children: content,
      span:
        typeof span === 'number'
          ? { xs: 1, sm: Math.min(span, column) }
          : span ?? (index === items.length - 1 ? 'filled' : 1),
    };
  });
  return (
    <Descriptions
      {...props}
      className={['detail-table', className].filter(Boolean).join(' ')}
      bordered
      colon={false}
      column={{ xs: 1, sm: column }}
      items={renderedItems}
      styles={{ label: { fontSize: labelFontSize } }}
    />
  );
}
